import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface SmsTemplate {
  id: string;
  name: string;
  description?: string;
  message: string;
  template_variables?: string[];
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AVAILABLE_VARIABLES = [
  { var: '{{name}}', description: 'Nome do contato' },
  { var: '{{email}}', description: 'Email do contato' },
  { var: '{{phone}}', description: 'Telefone do contato' },
  { var: '{{products}}', description: 'Lista de produtos comprados' },
  { var: '{{product}}', description: 'Primeiro produto comprado' },
];

export default function SmsTemplates() {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SmsTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    message: '',
    category: 'other',
    is_active: true,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${getApiUrl()}/sms-templates`);
      setTemplates(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      setError('Erro ao carregar templates SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template?: SmsTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        message: template.message,
        category: template.category || 'other',
        is_active: template.is_active,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        message: '',
        category: 'other',
        is_active: true,
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setError(null);
    setSuccess(null);
  };

  const handleSaveTemplate = async () => {
    try {
      setError(null);
      
      if (!formData.name || !formData.message) {
        setError('Nome e mensagem são obrigatórios');
        return;
      }

      if (editingTemplate) {
        await axios.put(`${getApiUrl()}/sms-templates/${editingTemplate.id}`, formData);
        setSuccess('Template atualizado com sucesso!');
      } else {
        await axios.post(`${getApiUrl()}/sms-templates`, formData);
        setSuccess('Template criado com sucesso!');
      }

      await loadTemplates();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      setError(error.response?.data?.message || 'Erro ao salvar template');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este template?')) {
      return;
    }

    try {
      await axios.delete(`${getApiUrl()}/sms-templates/${id}`);
      setSuccess('Template deletado com sucesso!');
      await loadTemplates();
    } catch (error: any) {
      console.error('Erro ao deletar template:', error);
      setError(error.response?.data?.message || 'Erro ao deletar template');
    }
  };

  const insertVariable = (variable: string) => {
    setFormData({
      ...formData,
      message: formData.message + variable,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Templates SMS
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Mensagem</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum template encontrado. Crie um novo template para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>{template.description || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title={template.message}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {template.message}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.is_active ? 'Ativo' : 'Inativo'}
                      color={template.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{template.category || 'other'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(template)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTemplate(template.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de criação/edição */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Editar Template SMS' : 'Novo Template SMS'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Variáveis Disponíveis
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {AVAILABLE_VARIABLES.map((variable) => (
                  <Chip
                    key={variable.var}
                    label={variable.var}
                    onClick={() => insertVariable(variable.var)}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                    title={variable.description}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Mensagem"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              margin="normal"
              required
              multiline
              rows={4}
              helperText={`${formData.message.length} caracteres`}
            />

            <TextField
              fullWidth
              select
              label="Categoria"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              margin="normal"
              SelectProps={{
                native: true,
              }}
            >
              <option value="welcome">Boas-vindas</option>
              <option value="purchase">Compra</option>
              <option value="abandoned_cart">Carrinho Abandonado</option>
              <option value="reminder">Lembrete</option>
              <option value="other">Outro</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveTemplate} variant="contained" color="primary">
            {editingTemplate ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
